import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { TextField } from "../shared/FormField";
import { AddressAutocomplete } from "../customer/AddressAutocomplete";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", latitude: "", longitude: "" };

export function DriverFormDialog({ open, onOpenChange, onSaved, driver }) {
  const isEdit = Boolean(driver);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(driver ? {
      name: driver.name || "", email: driver.email || "",
      phone: driver.phone || "", address: driver.address || "",
      latitude: driver.latitude || "", longitude: driver.longitude || "",
    } : EMPTY_FORM);
    setErrors({});
  }, [open, driver]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleAddressSelect = (r) => {
    setForm((f) => ({
      ...f,
      address: r.address || r.label || f.address,
      latitude: r.latitude != null ? String(r.latitude) : f.latitude,
      longitude: r.longitude != null ? String(r.longitude) : f.longitude,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = {
      ...form,
      latitude: form.latitude || "0",
      longitude: form.longitude || "0",
    };
    try {
      if (isEdit) {
        await apiClient.put(`/drivers/${driver.id}`, payload);
        toast.success("Driver updated");
      } else {
        await apiClient.post("/drivers", payload);
        toast.success("Driver created");
      }
      onSaved();
    } catch (error) {
      setErrors(getFieldErrors(error));
      toast.error(getApiErrorMessage(error, "Could not save driver."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Driver" : "New Driver"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this driver's contact details." : "A login account is created automatically for this driver."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Name" required value={form.name} onChange={setField("name")} error={errors.name} testId="driver-form-name" />
            <TextField label="Phone" required value={form.phone} onChange={setField("phone")} error={errors.phone} testId="driver-form-phone" />
            <TextField label="Email" required type="email" value={form.email} onChange={setField("email")} error={errors.email} testId="driver-form-email" />
            <div className="col-span-2">
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                onSelect={handleAddressSelect}
                label="Address"
                id="driver-form-address-autocomplete"
                testIdPrefix="driver-form-address"
              />
              {errors.address && <p className="mt-1 text-xs text-danger" data-testid="driver-form-address-error">{errors.address}</p>}
              <p className="mt-1 text-xs text-text-muted">Pick a suggestion to auto-fill the endpoint coordinates below, or enter them manually.</p>
            </div>
            <TextField label="Endpoint Latitude" required value={form.latitude} onChange={setField("latitude")} error={errors.latitude} hint="Where this driver's route ends" testId="driver-form-latitude" />
            <TextField label="Endpoint Longitude" required value={form.longitude} onChange={setField("longitude")} error={errors.longitude} hint="Where this driver's route ends" testId="driver-form-longitude" />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="driver-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" variant="accent" disabled={saving} data-testid="driver-form-submit-button">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Driver"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
